import {
  GrassItem,
  RpmCategory,
  RpmCategoryWrite,
  RpmItem,
  RpmItemWrite,
  RpmNote,
  RpmWeek,
  RpmWeekReview,
  RpmYearSummary,
} from '@core/models';

import {
  GrassCatcherItemDto,
  RpmCategoryDto,
  RpmItemDto,
  RpmItemNoteDto,
  RpmSummaryDto,
  RpmWeekDto,
  RpmWeekReviewDto,
  SaveRpmCategoryDto,
  SaveRpmItemDto,
} from './rpm.dto';

/**
 * The ONE translation between the RPM API and the app's RPM models.
 *
 * Reads: `…Guid` becomes `…Id`, `rpmItemStatus` becomes `status`, and every
 * nullable list or text the contract allows is defaulted HERE, so past the
 * boundary the app's types are honest (`result` is '' rather than null, a block
 * with no actions has `[]`). The server's derived answers — `overdue`,
 * `estimatedMinutesTotal`, `weekOutcome`, a week's counts — are carried as they
 * came; nothing here works one out.
 *
 * Writes: the reverse naming, and nothing else. Trimming and the blank-is-null
 * rules are applied by the store when it builds the body, and again by the
 * server, which is the authority.
 */

export function rpmItemFromDto(d: RpmItemDto): RpmItem {
  return {
    id: d.rpmItemGuid,
    status: d.rpmItemStatus,
    priority: d.priority ?? null,
    title: d.title ?? '',
    result: d.result ?? '',
    purpose: d.purpose ?? '',
    dueDt: d.dueDt,
    categoryId: d.categoryGuid ?? null,
    overdue: !!d.overdue,
    estimatedMinutesTotal: d.estimatedMinutesTotal ?? null,
    actions: (d.actions ?? []).map((a) => ({
      id: a.rpmItemActionGuid,
      text: a.text ?? '',
      complete: !!a.complete,
      must: !!a.must,
      estimatedMinutes: a.estimatedMinutes ?? null,
      leverage: a.leverage ?? null,
    })),
    notes: (d.notes ?? []).map(rpmNoteFromDto),
    weekOutcome: d.weekOutcome ?? null,
  };
}

export function rpmNoteFromDto(d: RpmItemNoteDto): RpmNote {
  return { id: d.rpmItemNoteGuid, authorPersonaCode: d.authorPersonaCode, notedTs: d.notedTs, text: d.text ?? '' };
}

export function rpmCategoryFromDto(d: RpmCategoryDto): RpmCategory {
  return { id: d.categoryGuid, name: d.name ?? '', colorId: d.colorGuid ?? null, hex: d.color ?? null };
}

export function grassItemFromDto(d: GrassCatcherItemDto): GrassItem {
  return { id: d.itemGuid, text: d.text ?? '', categoryId: d.categoryGuid ?? null, capturedTs: d.capturedTs };
}

export function rpmReviewFromDto(d: RpmWeekReviewDto): RpmWeekReview {
  return { weekStartDt: d.weekStartDt, accomplished: d.accomplished ?? null, learned: d.learned ?? null, updatedTs: d.updatedTs };
}

export function rpmWeekFromDto(d: RpmWeekDto): RpmWeek {
  return {
    weekStartDt: d.weekStartDt,
    counts: {
      completed: d.counts?.completed ?? 0,
      open: d.counts?.open ?? 0,
      overdue: d.counts?.overdue ?? 0,
      deleted: d.counts?.deleted ?? 0,
    },
    items: (d.rpmItems ?? []).map(rpmItemFromDto),
    review: d.review ? rpmReviewFromDto(d.review) : null,
  };
}

export function rpmSummaryFromDto(d: RpmSummaryDto): RpmYearSummary {
  return {
    year: d.year,
    completedCount: d.completedCount ?? 0,
    completedOnTimeCount: d.completedOnTimeCount ?? 0,
    openCount: d.openCount ?? 0,
    overdueCount: d.overdueCount ?? 0,
    weeksReviewedCount: d.weeksReviewedCount ?? 0,
  };
}

/** The add / edit block body. Actions travel by guid: null is a new one. */
export function rpmItemToDto(w: RpmItemWrite): SaveRpmItemDto {
  return {
    priority: w.priority,
    title: w.title,
    result: w.result,
    purpose: w.purpose,
    dueDt: w.dueDt,
    categoryGuid: w.categoryId,
    actions: w.actions.map((a) => ({
      rpmItemActionGuid: a.id,
      text: a.text,
      must: a.must,
      estimatedMinutes: a.estimatedMinutes,
      leverage: a.leverage,
    })),
  };
}

export function rpmCategoryToDto(w: RpmCategoryWrite): SaveRpmCategoryDto {
  return { name: w.name, colorGuid: w.colorId };
}
